import type { CH315KeyboardProtocol } from '../protocols'

const MAX_BULK_READ = 496

/**
 * Feature 私有读适配器。
 *
 * CH315 的公开协议一次最多读 496 B 且要求 4 B 对齐；这里仅负责按顺序分包，
 * 不提供块写，避免在 Checksum32 算法尚未确认时产生不可用配置。
 */
export class CH315FeatureMemory {
  constructor(private readonly protocol: CH315KeyboardProtocol) {}

  async read(address: number, length: number): Promise<Uint8Array> {
    if (address % 4 !== 0 || length < 0 || length % 4 !== 0) {
      throw new RangeError('CH315 feature reads require 4-byte aligned address and length')
    }

    const result = new Uint8Array(length)
    for (let offset = 0; offset < length; offset += MAX_BULK_READ) {
      const chunkLength = Math.min(MAX_BULK_READ, length - offset)
      const chunk = await this.protocol.readFlashBulk(address + offset, chunkLength)
      if (chunk.address !== address + offset || chunk.data.byteLength < chunkLength) {
        throw new Error(`CH315 short Flash read at 0x${(address + offset).toString(16)}`)
      }
      result.set(chunk.data.subarray(0, chunkLength), offset)
    }
    return result
  }
}

export function decodeCH315Text(bytes: Uint8Array): string {
  const end = bytes.findIndex((value) => value === 0 || value === 0xff)
  const content = end < 0 ? bytes : bytes.subarray(0, end)
  return new TextDecoder().decode(content)
}

export function readUint16LE(bytes: Uint8Array, offset: number): number {
  return bytes[offset] | (bytes[offset + 1] << 8)
}
